import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown, MapPin, Mail, Clock, Award, Star, Facebook, Sparkles } from "lucide-react";
import { BUSINESS, NAV, LOGO, MATERIALS, SUB_SERVICES } from "@/data/content";
import { DEEP_SERVICES } from "@/data/deepServices";
import { ScrollCTABar } from "@/components/ScrollCTABar";

const NavLink = ({ to, children, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    data-testid={`nav-link-${to.replace(/\//g, "-")}`}
    className="text-ink hover:text-brand font-semibold text-sm tracking-tight transition-colors"
  >
    {children}
  </Link>
);

export function Header() {
  const [open, setOpen] = React.useState(false);
  const [mobileSection, setMobileSection] = React.useState(null); // "services" | "areas" | "more"

  React.useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  // Reset mobile accordions when menu closes
  React.useEffect(() => {
    if (!open) setMobileSection(null);
  }, [open]);

  const toggleMobile = (key) => setMobileSection((prev) => (prev === key ? null : key));

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-ink-line">
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-home-link">
          <div className="bg-ink flex items-center justify-center rounded-sm p-1.5 h-11 w-11 md:h-12 md:w-12">
            <img src={LOGO} alt="Goral Construction LLC" className="h-full w-auto object-contain" />
          </div>
          <div className="leading-tight hidden md:block">
            <div className="font-heading font-extrabold text-ink text-base tracking-tight">Goral Construction</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft">Waterford, MI · Since {BUSINESS.founded}</div>
          </div>
        </Link>

        {/* Slim desktop nav - 4 top-level items for max focus on phone + CTA.
            NOTE: dropdown panels are always rendered in the DOM (CSS-only hide/show
            via group-hover + focus-within) so crawlers and prerender pick up every
            internal link without needing to trigger React state. This fixes the
            "orphaned pages" Semrush warnings for service-area and service slugs. */}
        <nav className="hidden lg:flex items-center gap-6 xl:gap-7">
          <div className="relative group">
            <button className="flex items-center gap-1 text-sm font-semibold text-ink hover:text-brand" data-testid="nav-services-dropdown">
              Services <ChevronDown className="w-4 h-4" />
            </button>
            <div className="absolute left-0 top-full pt-2 w-72 invisible opacity-0 group-hover:visible group-hover:opacity-100 focus-within:visible focus-within:opacity-100 transition-opacity">
              <div className="bg-white border border-ink-line rounded-sm shadow-xl p-2">
                {NAV.services.map((s) => (
                  <Link key={s.to} to={s.to} className="block px-3 py-2 text-sm text-ink hover:bg-bg-soft rounded-sm font-medium" data-testid={`dropdown-service-${s.to}`}>
                    {s.label}
                  </Link>
                ))}
                <div className="my-1 border-t border-ink-line" />
                <Link to="/services" className="block px-3 py-2 text-sm text-brand hover:bg-bg-soft rounded-sm font-semibold">All Services →</Link>
                <Link to="/materials" className="block px-3 py-2 text-sm text-ink hover:bg-bg-soft rounded-sm font-medium">Roofing Materials</Link>
                <Link to="/our-work" className="block px-3 py-2 text-sm text-ink hover:bg-bg-soft rounded-sm font-medium">View Our Work →</Link>
              </div>
            </div>
          </div>

          <div className="relative group">
            <button className="flex items-center gap-1 text-sm font-semibold text-ink hover:text-brand" data-testid="nav-areas-dropdown">
              Service Areas <ChevronDown className="w-4 h-4" />
            </button>
            <div className="absolute left-0 top-full pt-2 w-64 invisible opacity-0 group-hover:visible group-hover:opacity-100 focus-within:visible focus-within:opacity-100 transition-opacity">
              <div className="bg-white border border-ink-line rounded-sm shadow-xl p-2 max-h-80 overflow-y-auto">
                {NAV.cities.map((c) => (
                  <Link key={c.to} to={c.to} className="block px-3 py-2 text-sm text-ink hover:bg-bg-soft rounded-sm font-medium">
                    {c.label}, MI
                  </Link>
                ))}
                <div className="my-1 border-t border-ink-line" />
                <Link to="/service-areas" className="block px-3 py-2 text-sm text-brand hover:bg-bg-soft rounded-sm font-semibold">All Areas →</Link>
              </div>
            </div>
          </div>

          <NavLink to="/roof-cost-calculator">Cost Calculator</NavLink>
          <Link
            to="/roof-designer"
            data-testid="nav-link-roof-designer"
            className="inline-flex items-center gap-1.5 text-brand hover:text-ink font-bold text-sm tracking-tight transition-colors"
          >
            <Sparkles className="w-4 h-4" /> Roof Designer
          </Link>
          <NavLink to="/contact">Contact</NavLink>
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Phone - prominent on desktop (big, orange on hover, has phone badge on tablet+) */}
          <a href={`tel:${BUSINESS.phoneTel}`} className="hidden md:flex items-center gap-2 pl-3 pr-4 py-2 border border-ink-line rounded-sm hover:border-brand hover:text-brand hover:bg-brand/5 transition-colors text-sm font-bold text-ink" data-testid="header-phone-link">
            <Phone className="w-4 h-4 text-brand" /> <span className="tabular-nums">{BUSINESS.phone}</span>
          </a>
          <div className="hidden md:inline-flex">
            <Link to="/get-estimate" className="btn-primary text-sm" data-testid="header-estimate-cta">
              Get Free Estimate
            </Link>
          </div>

          {/* Mobile: compact phone icon + menu toggle. Phone is ALWAYS visible.
              Both buttons are 44×44px to meet WCAG 2.5.5 / Apple HIG touch target. */}
          <a href={`tel:${BUSINESS.phoneTel}`} className="md:hidden flex items-center justify-center w-11 h-11 bg-brand text-white rounded-sm" aria-label={`Call ${BUSINESS.phone}`} data-testid="mobile-header-call">
            <Phone className="w-4 h-4" />
          </a>
          <button className="lg:hidden w-11 h-11 grid place-items-center text-ink" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-ink-line bg-white max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="container-x py-3 flex flex-col divide-y divide-ink-line/60">
            {/* Services accordion */}
            <MobileAccordion label="Services" isOpen={mobileSection === "services"} onToggle={() => toggleMobile("services")} testId="mobile-acc-services">
              <div className="max-h-72 overflow-y-auto pr-1">
                {NAV.services.map((s) => (
                  <Link key={s.to} to={s.to} onClick={() => setOpen(false)} className="block py-2.5 text-sm text-ink font-medium hover:text-brand">{s.label}</Link>
                ))}
                <div className="border-t border-ink-line/50 my-2" />
                <Link to="/services" onClick={() => setOpen(false)} className="block py-2.5 text-sm text-brand font-bold">All Services →</Link>
              </div>
            </MobileAccordion>

            {/* Areas accordion */}
            <MobileAccordion label="Service Areas" isOpen={mobileSection === "areas"} onToggle={() => toggleMobile("areas")} testId="mobile-acc-areas">
              <div className="max-h-72 overflow-y-auto pr-1">
                {NAV.cities.map((c) => (
                  <Link key={c.to} to={c.to} onClick={() => setOpen(false)} className="block py-2.5 text-sm text-ink font-medium hover:text-brand">{c.label}, MI</Link>
                ))}
                <div className="border-t border-ink-line/50 my-2" />
                <Link to="/service-areas" onClick={() => setOpen(false)} className="block py-2.5 text-sm text-brand font-bold">All Areas →</Link>
              </div>
            </MobileAccordion>

            {/* Direct links */}
            <MobileLink to="/roof-cost-calculator" onClose={() => setOpen(false)}>Cost Calculator</MobileLink>
            <Link
              to="/roof-designer"
              onClick={() => setOpen(false)}
              data-testid="mobile-link-roof-designer"
              className="py-3 flex items-center gap-2 text-brand font-heading font-bold text-base hover:text-ink"
            >
              <Sparkles className="w-4 h-4" /> Roof Designer
            </Link>
            <MobileLink to="/free-roof-inspection" onClose={() => setOpen(false)}>Free Roof Inspection</MobileLink>
            <MobileLink to="/our-work" onClose={() => setOpen(false)}>Our Work</MobileLink>

            {/* More accordion - the secondary stuff */}
            <MobileAccordion label="More" isOpen={mobileSection === "more"} onToggle={() => toggleMobile("more")} testId="mobile-acc-more">
              <div className="max-h-72 overflow-y-auto pr-1">
                <Link to="/materials" onClick={() => setOpen(false)} className="block py-2.5 text-sm text-ink font-medium">Materials</Link>
                <Link to="/reviews" onClick={() => setOpen(false)} className="block py-2.5 text-sm text-ink font-medium">Reviews</Link>
                <Link to="/about" onClick={() => setOpen(false)} className="block py-2.5 text-sm text-ink font-medium">About</Link>
                <Link to="/blog" onClick={() => setOpen(false)} className="block py-2.5 text-sm text-ink font-medium">Blog</Link>
                <Link to="/financing" onClick={() => setOpen(false)} className="block py-2.5 text-sm text-ink font-medium">Financing</Link>
                <Link to="/faq" onClick={() => setOpen(false)} className="block py-2.5 text-sm text-ink font-medium">FAQ</Link>
                <Link to="/contact" onClick={() => setOpen(false)} className="block py-2.5 text-sm text-ink font-medium">Contact</Link>
              </div>
            </MobileAccordion>

            {/* Contact footer inside the menu */}
            <div className="pt-4 flex flex-col gap-2">
              <a href={`tel:${BUSINESS.phoneTel}`} className="btn-primary justify-center w-full" data-testid="mobile-menu-phone">
                <Phone className="w-4 h-4" /> {BUSINESS.phone}
              </a>
              <Link to="/get-estimate" onClick={() => setOpen(false)} className="btn-secondary justify-center w-full">
                Get Free Estimate
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

// Collapsible accordion for mobile nav
function MobileAccordion({ label, isOpen, onToggle, children, testId }) {
  return (
    <div className="py-1">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between py-3 text-left text-ink font-heading font-bold text-base"
        data-testid={testId}
        aria-expanded={isOpen}
      >
        <span>{label}</span>
        <ChevronDown className={`w-4 h-4 text-ink-soft transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-[max-height] duration-300 ease-out ${isOpen ? "max-h-[360px]" : "max-h-0"}`}>
        <div className="pl-1 pb-2">{children}</div>
      </div>
    </div>
  );
}

// Direct link that matches accordion row styling
function MobileLink({ to, onClose, children }) {
  return (
    <Link
      to={to}
      onClick={onClose}
      className="py-3 block text-ink font-heading font-bold text-base hover:text-brand"
    >
      {children}
    </Link>
  );
}

export function MobileCTABar() {
  const loc = useLocation();
  // Hide on pages with their own primary CTAs (calculator has sticky controls, designer has bottom-sheet UX).
  const hiddenPaths = ["/get-estimate", "/roof-designer", "/roof-cost-calculator"];
  if (hiddenPaths.some((p) => loc.pathname.startsWith(p))) return null;
  return (
    <div
      role="region"
      aria-label="Quick contact actions"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-ink-line flex gap-2 px-3 pt-2.5 pb-[calc(env(safe-area-inset-bottom)+0.6rem)] shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.18)]"
    >
      <a
        href={`tel:${BUSINESS.phoneTel}`}
        aria-label={`Call Goral Construction at ${BUSINESS.phone}`}
        className="flex-1 inline-flex items-center justify-center gap-2 h-12 bg-white border border-brand text-brand font-bold text-[15px] active:scale-[0.98] transition-transform touch-manipulation"
        data-testid="mobile-call-btn"
      >
        <Phone className="w-4 h-4" /> Call
      </a>
      <Link
        to="/get-estimate"
        aria-label="Get a free roof estimate"
        className="flex-1 inline-flex items-center justify-center gap-2 h-12 bg-brand text-white font-bold text-[15px] active:scale-[0.98] transition-transform touch-manipulation shadow-md"
        data-testid="mobile-estimate-btn"
      >
        Free estimate
      </Link>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="bg-bg-dark text-white mt-20 relative overflow-hidden">
      <div className="container-x pt-20 pb-10 relative">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-10">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3">
              <img src={LOGO} alt="Goral Construction LLC" className="h-12 w-auto" />
              <div>
                <div className="font-heading font-extrabold text-lg">Goral Construction</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted">Waterford, MI · Est. {BUSINESS.founded}</div>
              </div>
            </div>
            <p className="text-ink-muted text-sm mt-4 leading-relaxed">
              Family-owned roofing, siding & gutters serving Oakland County, Michigan since 2010.
            </p>
            <div className="flex items-center gap-1 mt-4">
              {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 fill-brand text-brand" />)}
              <span className="ml-2 text-sm text-white">5.0 on Angi · {BUSINESS.rating.count} verified reviews</span>
            </div>
            <div className="flex items-center gap-2 mt-5">
              <a href={BUSINESS.social.google} target="_blank" rel="noopener noreferrer" aria-label="Goral Construction on Google Maps" className="w-10 h-10 flex items-center justify-center border border-white/20 hover:border-brand hover:text-brand transition-colors rounded-sm" data-testid="social-google">
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 0 1 0-5 2.5 2.5 0 0 1 0 5z"/></svg>
              </a>
              <a href={BUSINESS.social.angi} target="_blank" rel="noopener noreferrer" aria-label="Goral Construction on Angi" className="h-10 px-3 flex items-center justify-center border border-white/20 hover:border-brand hover:text-brand transition-colors rounded-sm text-xs font-bold" data-testid="social-angi">
                ANGI 5.0
              </a>
              <a href={BUSINESS.social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Goral Construction on Facebook" className="w-10 h-10 flex items-center justify-center border border-white/20 hover:border-brand hover:text-brand transition-colors rounded-sm" data-testid="social-facebook">
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>
          <div>
            <div className="overline text-ink-muted mb-4">Services</div>
            <ul className="space-y-2">
              {NAV.services.map(s => (
                <li key={s.to}><Link to={s.to} className="text-sm text-white/85 hover:text-brand">{s.label}</Link></li>
              ))}
              <li><Link to="/services" className="text-sm text-brand hover:text-white font-semibold">All Services →</Link></li>
            </ul>
            {/* Specialty repair pages — every DEEP_SERVICES + SUB_SERVICES slug
                gets a sitewide footer anchor here so Semrush / Google can no
                longer flag these as orphaned (was 140 orphan-page warnings). */}
            <div className="overline text-ink-muted mt-6 mb-3">Specialty Repairs</div>
            <ul className="space-y-2">
              {DEEP_SERVICES.map(d => (
                <li key={d.slug}><Link to={`/services/${d.slug}`} className="text-sm text-white/70 hover:text-brand">{d.name}</Link></li>
              ))}
              {SUB_SERVICES.map(s => (
                <li key={s.slug}><Link to={`/services/${s.slug}`} className="text-sm text-white/70 hover:text-brand">{s.name}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            {/* All 13 service-area cities are linked here so the city pages (and through
                them, the 72 service×city combo pages) are never orphaned for crawlers. */}
            <div className="overline text-ink-muted mb-4">Service Areas</div>
            <ul className="space-y-2">
              {NAV.cities.map(c => (
                <li key={c.to}><Link to={c.to} className="text-sm text-white/85 hover:text-brand">{c.label}, MI</Link></li>
              ))}
              <li><Link to="/service-areas" className="text-sm text-brand hover:text-white font-semibold">All Areas →</Link></li>
            </ul>
          </div>
          <div>
            {/* Resources column: ensures /materials/*, /faq, /financing, /blog,
                /roof-cost-calculator, /roof-designer, /about/maciej-ogonowski
                and other previously-orphaned pages have at least one site-wide
                internal link, satisfying Semrush's orphan-page audit. */}
            <div className="overline text-ink-muted mb-4">Resources</div>
            <ul className="space-y-2">
              <li><Link to="/roof-cost-calculator" className="text-sm text-white/85 hover:text-brand">Roof Cost Calculator</Link></li>
              <li><Link to="/roof-designer" className="text-sm text-white/85 hover:text-brand">AI Roof Designer</Link></li>
              <li><Link to="/free-roof-inspection" className="text-sm text-white/85 hover:text-brand">Free Roof Inspection</Link></li>
              <li><Link to="/our-work" className="text-sm text-white/85 hover:text-brand">Our Work</Link></li>
              <li><Link to="/reviews" className="text-sm text-white/85 hover:text-brand">Reviews</Link></li>
              <li><Link to="/blog" className="text-sm text-white/85 hover:text-brand">Roofing Blog</Link></li>
              <li><Link to="/faq" className="text-sm text-white/85 hover:text-brand">FAQ</Link></li>
              <li><Link to="/financing" className="text-sm text-white/85 hover:text-brand">Financing</Link></li>
              <li><Link to="/about/maciej-ogonowski" className="text-sm text-white/85 hover:text-brand">Meet Maciej Ogonowski</Link></li>
            </ul>
            <div className="overline text-ink-muted mt-6 mb-3">Materials</div>
            <ul className="space-y-2">
              {MATERIALS.map(m => (
                <li key={m.slug}><Link to={`/materials/${m.slug}`} className="text-sm text-white/85 hover:text-brand">{m.name}</Link></li>
              ))}
              <li><Link to="/materials" className="text-sm text-brand hover:text-white font-semibold">All Materials →</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline text-ink-muted mb-4">Contact</div>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-2 items-start"><Phone className="w-4 h-4 mt-0.5 text-brand" /><a href={`tel:${BUSINESS.phoneTel}`} className="text-white/85 hover:text-brand">{BUSINESS.phone}</a></li>
              <li className="flex gap-2 items-start"><Mail className="w-4 h-4 mt-0.5 text-brand" /><a href={`mailto:${BUSINESS.email}`} className="text-white/85 hover:text-brand break-all">{BUSINESS.email}</a></li>
              <li className="flex gap-2 items-start"><MapPin className="w-4 h-4 mt-0.5 text-brand" /><span className="text-white/85">{BUSINESS.address.full}</span></li>
              <li className="flex gap-2 items-start"><Clock className="w-4 h-4 mt-0.5 text-brand" /><span className="text-white/85">{BUSINESS.hours.weekdays}<br/>{BUSINESS.hours.sunday}<br/><span className="text-brand font-semibold">{BUSINESS.hours.emergency}</span></span></li>
            </ul>
            <div className="mt-4 flex items-center gap-2 text-xs text-ink-muted"><Award className="w-4 h-4 text-brand" /> Licensed · Insured · Master Certified</div>
            <Link to="/get-estimate" className="btn-primary text-sm mt-5 inline-flex">Get Free Estimate</Link>
          </div>
        </div>
        <div className="mt-14 pt-8 border-t border-ink-muted/30">
          <p className="text-xs text-ink-muted leading-relaxed max-w-5xl">
            <strong className="text-white/90">Goral Construction LLC</strong> is a family-owned, licensed and insured roofing contractor located at {BUSINESS.address.full}. Founded in {BUSINESS.founded} by Maciej Ogonowski (28+ years of Michigan roofing experience), we install asphalt shingle, metal, flat, tile and slate roofing, siding, seamless gutters, and handle insurance claims across Oakland County, Michigan - including Waterford, Clarkston, White Lake, Commerce Township, West Bloomfield, Bloomfield Hills, Rochester Hills, Troy, Auburn Hills, Birmingham, Pontiac, Royal Oak and Lake Orion. Rated 5.0/5 on Angi with {BUSINESS.rating.count}+ verified five-star reviews. Call {BUSINESS.phone} for a free estimate.
          </p>
        </div>
        <div className="mt-10 pt-6 border-t border-ink-muted/30 flex flex-col md:flex-row justify-between gap-4 text-xs text-ink-muted">
          <div>© {new Date().getFullYear()} Goral Construction LLC. All rights reserved.</div>
          <div className="flex gap-4">
            <Link to="/privacy-policy" className="hover:text-white">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-white">Terms of Service</Link>
            <Link to="/financing" className="hover:text-white">Financing</Link>
            <Link to="/faq" className="hover:text-white">FAQ</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function Layout({ children }) {
  return (
    <>
      <a href="#main-content" className="skip-to-main">Skip to main content</a>
      <ScrollCTABar />
      <Header />
      <main id="main-content" className="pb-20 md:pb-0">{children}</main>
      <Footer />
      <div className="mobile-cta-space" />
      <MobileCTABar />
    </>
  );
}
